import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { FiFileText, FiDownload, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import AppLayout from '../../components/layout/AppLayout';
import PageHeader from '../../components/ui/PageHeader';
import Button from '../../components/ui/Button';
import DataTable from '../../components/ui/DataTable';
import StatusPill from '../../components/ui/StatusPill';
import ErrorBanner from '../../components/ui/ErrorBanner';
import EmptyState from '../../components/ui/EmptyState';
import CurrencyDisplay from '../../components/shared/CurrencyDisplay';
import DateDisplay from '../../components/shared/DateDisplay';
import { listMyPayroll, downloadPayrollSlipPdf } from '../../api/payroll';
import Modal from '../../components/ui/Modal';
import PayslipDocument, { getDeductionTotal, getGrossTotal, getTakeHome } from './PayslipDocument';

export default function MyPayrollPage() {
  const { data: rawPayrolls, isLoading, error, refetch } = useQuery({
    queryKey: ['payroll', 'my'],
    queryFn: listMyPayroll,
  });

  const payrolls = React.useMemo(() => (
    Array.isArray(rawPayrolls) ? rawPayrolls : (Array.isArray(rawPayrolls?.data) ? rawPayrolls.data : [])
  ), [rawPayrolls]);
  
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(10);
  const [selectedPayslip, setSelectedPayslip] = React.useState(null);
  const [isPdfLoading, setIsPdfLoading] = React.useState(false);

  const handleDownloadPdf = async (payroll) => {
    if (!payroll?.id) return;
    setIsPdfLoading(true);
    try {
      const month = String(payroll.month || '').padStart(2, '0');
      const filename = `salary_slip_${month}_${payroll.year}_${payroll.employeeId || payroll.id}.pdf`;
      await downloadPayrollSlipPdf(payroll.id, filename);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('PDF download failed', err);
    } finally {
      setIsPdfLoading(false);
    }
  };

  const paginated = React.useMemo(() => {
    const start = page * pageSize;
    return payrolls.slice(start, start + pageSize);
  }, [payrolls, page, pageSize]);

  const totalPages = Math.ceil(payrolls.length / pageSize) || 1;

  const columns = [
    {
      key: 'period',
      header: 'Period',
      render: (r) => {
        const date = new Date(r.year, r.month - 1);
        const period = date.toLocaleString('default', { month: 'short', year: 'numeric' });
        return <span className="font-medium text-slate-800">{period}</span>;
      },
    },
    {
      key: 'totalCtc',
      header: 'CTC / Gross',
      align: 'right',
      render: (r) => <CurrencyDisplay value={getGrossTotal(r)} className="font-medium text-slate-800" />,
    },
    {
      key: 'deductions',
      header: 'Deductions',
      align: 'right',
      render: (r) => <span className="text-red-500"><CurrencyDisplay value={getDeductionTotal(r)} /></span>,
    },
    {
      key: 'takeHomeSalary',
      header: 'Take Home',
      align: 'right',
      render: (r) => <CurrencyDisplay value={getTakeHome(r)} className="font-bold text-green-700" />,
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) => {
        const val = r.paid ? 'PAID' : r.status;
        return <StatusPill value={val} />;
      },
    },
    {
      key: 'createdAt',
      header: 'Generated',
      render: (r) => <DateDisplay value={r.createdAt} />,
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      render: (r) => (
        <Button variant="ghost" size="sm" icon={FiDownload} onClick={() => handleDownloadPdf(r)}>
          Slip
        </Button>
      ),
    },
  ];

  return (
    <AppLayout title="My Payroll" breadcrumb="My Profile / Payroll">
      <style>{`
        @page {
          size: A4 portrait;
          margin: 8mm;
        }
        @media print {
          html, body {
            width: 210mm !important;
            min-height: 297mm !important;
            margin: 0 !important;
            background: white !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          body * { visibility: hidden; }
          #printable-payslip, #printable-payslip * { visibility: visible; }
          #printable-payslip {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 194mm !important;
            min-width: 194mm !important;
            max-width: 194mm !important;
            box-sizing: border-box !important;
            margin: 0 !important;
            padding: 12mm 14mm 8mm !important;
            background: white !important;
            border: none !important;
            box-shadow: none !important;
          }
          .no-print { display: none !important; }
        }
      `}</style>
      <PageHeader
        title="My Payslips"
        description="View your salary history and download payslips."
      />

      {error && (
        <div className="mb-6">
          <ErrorBanner error={error} title="Failed to load payroll data" onRetry={refetch} />
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
        <DataTable
          columns={columns}
          rows={paginated}
          loading={isLoading}
          keyField="id"
          empty={
            <EmptyState
              icon={FiFileText}
              title="No payslips available"
              description="You do not have any payroll records at this time."
            />
          }
        />

        {payrolls.length > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between border-t border-slate-200 bg-slate-50 px-6 py-3">
            <div className="flex items-center gap-3 mb-3 sm:mb-0">
              <span className="text-sm text-slate-500">
                Page Size:
              </span>
              <select 
                value={pageSize} 
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(0);
                }}
                className="w-20 px-2 py-1 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary outline-none"
              >
                <option value={10}>10 / page</option>
                <option value={20}>20 / page</option>
                <option value={50}>50 / page</option>
                <option value={100}>100 / page</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500 mr-2">
                Page <span className="font-medium text-slate-700">{page + 1}</span> of <span className="font-medium text-slate-700">{totalPages}</span>
              </span>
              <Button 
                variant="outline" 
                size="sm" 
                icon={FiChevronLeft} 
                disabled={page === 0} 
                onClick={() => setPage(p => Math.max(0, p - 1))}
              >
                Previous
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                disabled={page >= totalPages - 1} 
                onClick={() => setPage(p => p + 1)}
              >
                Next <FiChevronRight className="ml-1" />
              </Button>
            </div>
          </div>
        )}
      </div>

      <Modal open={!!selectedPayslip} onClose={() => setSelectedPayslip(null)} title="Payslip Preview" size="xl">
        {selectedPayslip && (
          <div className="p-4" style={{ maxWidth: '1000px', margin: '0 auto', width: '100%', overflowX: 'auto' }}>
            <PayslipDocument payroll={selectedPayslip} />
            <div className="mt-8 flex justify-end gap-3">
              <Button variant="outline" onClick={() => setSelectedPayslip(null)}>Close</Button>
              <Button
                variant="primary"
                icon={FiDownload}
                loading={isPdfLoading}
                onClick={() => handleDownloadPdf(selectedPayslip)}
              >
                Download PDF
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </AppLayout>
  );
}
